import React, { useContext, useEffect, useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt as ReceiptIcon } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Checkbox from '@material-ui/core/Checkbox';
import { Grid } from '@material-ui/core';
import { useKeycloak } from '@react-keycloak/web';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import { Form, FormSpy } from 'react-final-form';
import axios from 'axios';
import ImportSdaContext from '../../../../contexts/registrations/import-sda/ImportSdaContext';
import { fetchAllAsync, selectImportSdaList } from '../../../../features/import-sda/importSdaSlice';
import QCXSimpleConsultPageTemplate from '../../../../templates/simple-consult-page/QCXSimpleConsultPageTemplate';
import { formatCnpj } from '../../../../utils/hooks/form/field/formatters';
import { formatDate } from '../../../../utils/general/general-utils';
import QCXDatePicker from '../../../../shared-components/date-picker/QCXDatePicker';
import QCXSelectDespachanteAutocomplete from '../../../../shared-components/select-despachante/QCXSelectDespachanteAutocomplete';
import QCXSelectModeloTabelaSdautocomplete from '../../../../shared-components/select-modelo-tabela-sda/QCXSelectModeloTabelaSdaAutocomplete';
import { setErrorFeedback, setSuccessFeedback } from '../../../../features/feedback/feedbackSlice';
import { useUnidadeNegocioGuard } from '../../../../ts/common/hooks/useUnidadeNegocioGuard';
import { selectUnidadeSelecionada } from '../../../../features/usuario-logado/usuarioLogadoSlice';
import QCXFinalTextField from '../../../../shared-components/final-text-field/QCXFinalTextField';
import { focusLockActions } from '../../../../features/focus-lock/focusLockSlice';
import QCXAutocompleteSelect from '../../../../shared-components/autocomplete-select/QCXAutocompleteSelect';

export default function SDAExportConsultPage({ authInfo = {} }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const KEYCLOAK_TOKEN_TIMEOUT = 10;

  const { keycloak } = useKeycloak();
  const { token } = keycloak;
  keycloak.updateToken(KEYCLOAK_TOKEN_TIMEOUT);
  const tenant = useSelector(selectUnidadeSelecionada);
  useUnidadeNegocioGuard();



  const handleFocusInput = () => {
    dispatch(focusLockActions.unlock());
  };

  const handleBlurInput = () => {
    dispatch(focusLockActions.lock());
  };


  const importSdaList = useSelector(selectImportSdaList);
  const { status } = useContext(ImportSdaContext);
  const isLoading = useMemo(() => status === 'ALTERNATIVE_LOADING_STATUS', [status]);

  const breadcrumbs = [
    { link: { to: '/', name: t('com.muralis.qcx.inicio') } },
    { text: { name: 'Listagem SDA Export' }, default: true },
  ];

  const columns = useMemo(
    () => [
      {
        field: 'processo',
        headerName: t('com.muralis.qcx.processo'),
        align: 'center',
        headerAlign: 'center',
        flex: 150,
        valueGetter: ({ row }) => row?.processo || '-',
      },
      {
        field: 'cliente',
        headerName: t('com.muralis.qcx.importador.label'),
        align: 'center',
        headerAlign: 'center',
        flex: 150,
        valueGetter: ({ row }) => row?.cliente || '-',
      },
      {
        field: 'cnpj',
        headerName: t('com.muralis.qcx.empresa.CNPJ'),
        align: 'center',
        headerAlign: 'center',
        flex: 120,
        valueGetter: ({ row }) => (row?.cnpj ? formatCnpj(row.cnpj) : '-'),
      },
      {
        field: 'valorSda',
        headerName: 'Valor SDA',
        align: 'center',
        headerAlign: 'center',
        flex: 100,
        valueGetter: ({ row }) =>
          row?.valorSda != null ? row.valorSda.toFixed(2) : '-',
      },
      {
        field: 'dataAbertura',
        headerName: t('com.muralis.qcx.dataAbertura'),
        align: 'center',
        headerAlign: 'center',
        flex: 100,
        valueGetter: ({ row }) =>
          row?.dataAbertura ? row.dataAbertura : '-',
      },
      {
        field: 'dataRegistro',
        headerName: t('com.muralis.qcx.dataRegistro'),
        align: 'center',
        headerAlign: 'center',
        flex: 100,
        valueGetter: ({ row }) =>
          row?.dataRegistro ? row.dataRegistro : '-',
      },
      {
        field: 'transmitida',
        headerName: 'Transmitida',
        align: 'center',
        headerAlign: 'center',
        flex: 80,
        valueGetter: ({ row }) => (row?.transmitida ? 'Sim' : 'Não'),
      },
      {
        field: 'id',
        headerName: 'Código',
        align: 'center',
        headerAlign: 'center',
        flex: 150,
        valueGetter: ({ row }) => row?.id || '-',
      },
    ],
    [t]
  );

  const filterPropGetter = useCallback(
    (row) => `${row.processo} ${row.cliente} ${row.cnpj}`,
    []
  );

  const [batchTransmitMode, setBatchTransmitMode] = useState(false);
  const [selectedSdaIds, setSelectedSdaIds] = useState([]);
  const [retransmitir, setRetransmitir] = useState(false);
  const [gerarSolicitacaoPagamento, setGerarSolicitacaoPagamento] = useState(false);
  const [dataValidade, setDataValidade] = useState(null);
  const [despachante, setDespachante] = useState(null);
  const [modeloTabelaSda, setModeloTabelaSda] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [defaultValue, setDefaultValue] = useState('');
  const [tipoSda, setTipoSda] = useState(null);
  const listValores = [
    { id: 'SINDASP', value: "SINDASP" },
    { id: 'ADAB', value: "ADAB" }
  ];

  useEffect(() => { setDefaultValue({ id: null, value: '' }) }, [])

  const handleSelectionModelChange = useCallback((newSelection) => {
    setSelectedSdaIds(newSelection?.selectionModel ?? newSelection);
  }, []);

  // lista colada de uma planilha ou digitada: um processo por linha, ou separados por vírgula/espaço
  const [listaProcessosTexto, setListaProcessosTexto] = useState('');
  const listaProcessos = useMemo(
    () => [...new Set(
      listaProcessosTexto
        .split(/[\s,;]+/)
        .map((processo) => processo.trim().toUpperCase())
        .filter(Boolean)
    )],
    [listaProcessosTexto]
  );

  const handleTransmitSelected = useCallback(
    async (values) => {
      if (!selectedSdaIds || selectedSdaIds.length === 0) {
        alert('Selecione pelo menos um processo para gerar o arquivo.');
        return;
      }
      if (!dataValidade) {
        dispatch(
          setErrorFeedback({
            message: "O campo 'Data de Validade' é obrigatório.",
          })
        );
        return;
      }
      if (!values.tipoSda) {
        dispatch(
          setErrorFeedback({
            message: "O campo 'Tipo SDA' é obrigatório.",
          })
        );
        return;
      }

      if (!despachante && values.tipoSda !== 'ADAB') {
        dispatch(
          setErrorFeedback({
            message: "O campo 'Despachante' é obrigatório.",
          })
        );
        return;
      }
      if (!modeloTabelaSda && values.tipoSda !== 'ADAB') {
        dispatch(
          setErrorFeedback({
            message: "O campo 'Modelo de Tabela SDA' é obrigatório.",
          })
        );
        return;
      }

      const ids =
        selectedSdaIds && selectedSdaIds.selectionModel
          ? selectedSdaIds.selectionModel
          : selectedSdaIds;

      const formattedDate = dataValidade
        ? dataValidade.format('DD/MM/YYYY')
        : null;

      const payload = {
        idModeloSda: modeloTabelaSda?.id,
        dataValidade: formattedDate,
        idDespachante: despachante?.id,
        idProcessos: ids,
        tipo: 'EXPORTACAO',
        observacao: values.observacao || '',
        tipoSda: tipoSda?.value || '',
        gerarSolicitacaoPagamento,
      };

      try {
        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
            quickcomexTenant: tenant,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        };

        const baseUrl = `${process.env.REACT_APP_QCX_API_BASE_URL}/tabelasda/gerar-txt`;
        const response = await axios.post(baseUrl, payload, config);

        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const ext = values.tipoSda === 'ADAB' ? 'json' : 'txt';
        link.setAttribute('download', `${values.nomeArquivo || 'SDA'}.${ext}`);
        document.body.appendChild(link);
        link.click();

        const spStatus = response.headers['x-sp-status'];
        let formatted = '';
        const spMessage = response.headers['x-sp-message']
          ? decodeURIComponent(response.headers['x-sp-message'])
          : '';
        if (spMessage) {
          const regex = /\+/g;
          formatted = spMessage.replace(regex, ' ');
        }

        if (spStatus === 'ERROR') {
          dispatch(setErrorFeedback({
            message: `TXT gerado, mas houve erro na geração da Solicitação de Pagamento: ${formatted || 'Erro desconhecido.'}`,
          }));
        } else {
          /* eslint-disable-next-line no-lonely-if */
          if (gerarSolicitacaoPagamento) {
            dispatch(setSuccessFeedback({ message: 'TXT e Solicitação de Pagamento gerados com sucesso!' }));
          } else {
            dispatch(setSuccessFeedback({ message: 'Arquivo gerado com sucesso!' }));
          }
        }
        dispatch(fetchAllAsync('EXPORTACAO'));
      } catch (error) {
        console.error('Erro ao gerar arquivo', error);
        dispatch(
          setErrorFeedback({
            message: 'Erro ao gerar arquivo. Tente novamente.',
          })
        );
      }
    },
    [
      selectedSdaIds,
      dataValidade,
      despachante,
      modeloTabelaSda,
      tipoSda,
      dispatch,
      token,
      tenant,
      gerarSolicitacaoPagamento
    ]
  );

  const controlButtons = useMemo(() => (
    batchTransmitMode
      ? [
        {
          description: 'Confirmar Transmitir',
          color: 'secondary',
          onClick: () => handleTransmitSelected(formValues),
          disabled: selectedSdaIds.length === 0,
        },
        {
          description: 'Cancelar',
          variant: 'outlined',
          onClick: () => setBatchTransmitMode(false),
        },
      ]
      : [
        {
          description: 'TRANSMITIR',
          color: 'primary',
          onClick: () => setBatchTransmitMode(true),
        },
      ]
  ), [batchTransmitMode, handleTransmitSelected, selectedSdaIds.length, formValues]);

  const filteredSdaList = useMemo(() => (
    retransmitir
      ? importSdaList.filter((item) => item.transmitida)
      : importSdaList.filter((item) => !item.transmitida)
  ), [importSdaList, retransmitir]);

  const sdaListPorProcessos = useMemo(() => {
    if (listaProcessos.length === 0) {
      return filteredSdaList;
    }
    const processos = new Set(listaProcessos);
    return filteredSdaList.filter((item) => processos.has(String(item?.processo || '').trim().toUpperCase()));
  }, [filteredSdaList, listaProcessos]);

  const processosNaoEncontrados = useMemo(() => {
    const encontrados = new Set(sdaListPorProcessos.map((item) => String(item?.processo || '').trim().toUpperCase()));
    return listaProcessos.filter((processo) => !encontrados.has(processo));
  }, [listaProcessos, sdaListPorProcessos]);

  // com uma lista informada, os processos encontrados já ficam marcados para gerar
  useEffect(() => {
    if (listaProcessos.length === 0) {
      return;
    }
    setSelectedSdaIds(sdaListPorProcessos.map((item) => item.id));
    if (sdaListPorProcessos.length > 0) {
      setBatchTransmitMode(true);
    }
  }, [listaProcessos, sdaListPorProcessos]);

  const templateProps = {
    page: {
      title: 'Listagem SDA Export',
      icon: <ReceiptIcon />,
      breadcrumbs,
    },
    control: {
      others: controlButtons,
    },
    table: {
      columns,
      rowsCount: sdaListPorProcessos.length,
      pageSize: 10,
      pagination: true,
      checkboxSelection: batchTransmitMode,
      selectionModel: selectedSdaIds,
      onSelectionModelChange: handleSelectionModelChange,
    },
  };

  useEffect(() => {
    dispatch(fetchAllAsync('EXPORTACAO'));
  }, [dispatch]);

  return (
    <QCXSimpleConsultPageTemplate
      pageProps={templateProps.page}
      controlProps={templateProps.control}
      tableProps={templateProps.table}
      tableData={sdaListPorProcessos}
      isLoading={isLoading}
      filterPropGetter={filterPropGetter}
      authInfo={authInfo}
      requiredRoles={['exportacao-sda']}
    >
      <Form
        onSubmit={(values) => handleTransmitSelected(values)}
        initialValues={{
          despachante: null,
          dataValidade: null,
          modeloTabelaSda: null,
          nomeArquivo: 'SDA',
        }}
      >
        {() => (
          <form>
            <FormSpy subscription={{ values: true }}>
              {({ values }) => {
                React.useEffect(() => {
                  setFormValues(values);
                }, [values]);
                return null;
              }}
            </FormSpy>

            <div style={{ marginTop: 30, padding: '0 20px' }}>
              <div style={{ display: 'flex', gap: 20 }}>
                <QCXDatePicker
                  label="Data de Vencimento"
                  name="dataValidade"
                  value={dataValidade}
                  onChange={(date) => setDataValidade(date)}
                  format="DD/MM/YYYY"
                  placeholder="DD/MM/YYYY"
                  style={{ flex: 1 }}
                />

                <QCXAutocompleteSelect
                  label="SINDICATO SDA"
                  id="tipoSda"
                  name="tipoSda"
                  options={listValores}
                  defaultValue={defaultValue}
                  getOptionLabel={(item) => (item.value ? item.value : '')}
                  onChange={(e, v) => setTipoSda(v)}
                  style={{ flex: 1 }}
                />

                <QCXFinalTextField
                  name="nomeArquivo"
                  label="Nome do Arquivo"
                  onFocus={handleFocusInput}
                  onBlur={handleBlurInput}
                  placeholder="Digite o nome do arquivo"
                  style={{ flex: 1.5 }}
                />
              </div>

              <div style={{ display: 'flex', gap: 20, marginTop: 20 }}>
                {tipoSda?.value === 'SINDASP' && (
                  <>
                    <QCXSelectDespachanteAutocomplete
                      label="Despachante"
                      name="despachante"
                      value={despachante}
                      onChange={(e, v) => setDespachante(v)}
                      style={{ flex: 1 }}
                    />
                    <QCXSelectModeloTabelaSdautocomplete
                      label="Modelo de Tabela SDA"
                      name="modeloTabelaSda"
                      value={modeloTabelaSda}
                      onChange={(e, v) => setModeloTabelaSda(v)}
                      tipo="EXPORTACAO"
                      style={{ flex: 1 }}
                    />
                  </>
                )}

                {tipoSda?.value === 'ADAB' && (
                  <QCXFinalTextField
                    name="observacao"
                    label="Observações"
                    placeholder="Digite suas observações"
                    multiline
                    minRows={6}
                    maxRows={10}
                    fullWidth
                    style={{ flex: 2 }}
                    onFocus={handleFocusInput}
                    onBlur={handleBlurInput}
                  />
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={retransmitir}
                      onChange={(e) => setRetransmitir(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="RETRANSMITIR"
                  style={{ display: 'flex', alignItems: 'center' }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={gerarSolicitacaoPagamento}
                      onChange={(e) => setGerarSolicitacaoPagamento(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="GERAR SOLICITAÇÃO DE PAGAMENTO"
                  style={{ display: 'flex', alignItems: 'center' }}
                />
              </div>

              <div style={{ marginTop: 20 }}>
                <TextField
                  id="lista-processos-sda"
                  label="Lista de processos"
                  placeholder="Cole ou digite os processos: um por linha, ou separados por vírgula ou espaço"
                  value={listaProcessosTexto}
                  onChange={(e) => setListaProcessosTexto(e.target.value)}
                  onFocus={handleFocusInput}
                  onBlur={handleBlurInput}
                  variant="outlined"
                  multiline
                  minRows={2}
                  maxRows={6}
                  fullWidth
                  helperText={
                    listaProcessos.length > 0
                      ? `${sdaListPorProcessos.length} de ${listaProcessos.length} processos encontrados e marcados` +
                        (processosNaoEncontrados.length > 0
                          ? `. Não encontrados: ${processosNaoEncontrados.join(', ')}`
                          : '')
                      : undefined
                  }
                  error={processosNaoEncontrados.length > 0}
                />
              </div>
            </div>
          </form>
        )}
      </Form>
    </QCXSimpleConsultPageTemplate>
  );
}
